import _ from "lodash";
import React, { useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { Popover } from "@toddle-edu/react-tiny-popover";
import classNames from "classnames";

import { useI18n } from "Hooks";
import {
  getSubText,
  getFormattedNameForUserType,
  CHANNEL_ACTIVITY_TYPES,
} from "Utils/MessageHelpers";
import { isIdEqual } from "Utils/idHelpers";
import { useMessageOptions } from "UIComponents/Message/providers/MessageProvider";

import UserNameAvatar from "AppComponents/UserNameAvatar/UserNameAvatar";
import UserAvatar, { UserName, AVATAR_RENDER_MODES } from "../UserAvatar";
import classes from "./MessageActivity.scss";

//in this order we will display activity to users
const activityOrder = ["staff", "student", "parent"];

const styles = {
  avatarStyles: {
    size: "small",
    randomizeColor: true,
    shade: "light",
    shape: "square",
  },
  customSubTextStyle: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    textWrap: "nowrap",
  },
};

const USER_TYPE_LOCALE = {
  staff: {
    key: "staff",
    locale: "common:staff_count_lowercase",
  },
  student: {
    key: "student",
    locale: "common:student_count",
  },
  parent: {
    key: "parent",
    locale: "common:family_member_count",
  },
};

export const getActivityText = ({ userGroupBy, index, t }) => {
  const key = _.keys(userGroupBy)[index];
  const value = _.values(userGroupBy)[index];
  const locale = USER_TYPE_LOCALE[key].locale;

  return {
    label: t(locale, {
      count: _.size(value),
    }),
    value,
  };
};

const UserDropdownPopover = ({ t, options }) => {
  const { defaultNameSortPreference, currentUserType } = useMessageOptions();
  return (
    <div className={classes.popoverRoot}>
      <div className={classes.popoverUserList}>
        {_.map(options, user => {
          const { id, name, value, profileImage, isArchived, type } = user;
          return (
            <div className={classes.userItem} key={id}>
              <UserNameAvatar
                id={value}
                profileImage={profileImage}
                name={name}
                viewMode={"NAME_AND_CUSTOM_SUBTEXT"}
                shouldDynamicColor={true}
                isArchived={isArchived}
                showAvatar={true}
                avatarStyles={styles.avatarStyles}
                showArchivedText={true}
                showTooltip={false}
                customSubTextStyle={styles.customSubTextStyle}
                customSubText={getSubText({
                  loggedInUserRole: currentUserType,
                  messagedUserObj: user,
                  messagedUserRole: type,
                  t,
                  defaultStudentNameSortPreference: defaultNameSortPreference,
                })}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const UsersDropdown = ({ options, children, t }) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const closePopover = () => setPopoverOpen(false);
  const openPopover = () => setPopoverOpen(true);

  return (
    <Popover
      isOpen={popoverOpen}
      onClickOutside={closePopover}
      containerClassName={classes.popoverContainer}
      positions={["right", "left", "top", "bottom"]} // preferred positions by priority
      content={<UserDropdownPopover t={t} options={options} />}
    >
      <div
        className={classNames("text-label-3", classes.link)}
        onClick={openPopover}
      >
        {children}
      </div>
    </Popover>
  );
};

const getUserListItems = ({
  users,
  defaultNameSortPreference,
  defaultStudentNameOrderPreference,
}) => {
  return _.map(users, user => {
    const fullName = getFormattedNameForUserType({
      user: user,
      orderBy: defaultNameSortPreference,
      defaultStudentNameOrderPreference,
      isFormattedNameV2: true,
    });

    const { id } = user;
    return {
      key: id,
      name: fullName,
      ...user,
    };
  });
};

const getUserByType = ({
  users,
  defaultNameSortPreference,
  defaultStudentNameOrderPreference,
}) => {
  const userGroupBy = _.groupBy(users, "type");
  const staff = _.get(userGroupBy, "staff", []);

  const student = _.get(userGroupBy, "student", []);

  const parent = _.get(userGroupBy, "parent", []);

  const userTypeOrderBy = _.reduce(
    activityOrder,
    (result, order) => {
      if (userGroupBy[order]) {
        result[order] = getUserListItems({
          users: _.get(userGroupBy, order, []),
          defaultNameSortPreference,
          defaultStudentNameOrderPreference,
        });
      }
      return result;
    },
    {}
  );

  return { staff, student, parent, userGroupBy: userTypeOrderBy };
};

const ActivityTextComponent = props => {
  const { targetUsers, createdBy, type = "ADD", activityTextClass } = props;

  const {
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
  } = useMessageOptions();

  const {
    staff = [],
    student = [],
    parent = [],
    userGroupBy = {},
  } = getUserByType({
    users: targetUsers,
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
  });
  const targetUserSize = _.size(targetUsers);

  const ifSingleTargetUser = targetUserSize === 1;

  const { t } = useI18n(["common"]);

  //based on type we will pick locale
  //of whether to add member or remove member
  const DESCRIPTION_TYPE_LOCALE_MAP = {
    ADD: {
      JOINED_LEFT_CHANNEL: "common:has_joined_the_channel",
      MULTIPLE_JOINED_LEFT_CHANNEL: "common:has_joined_the_channel_with_others",
      ALL_TYPE_USERS_ADDED_REMOVED: "common:added_member_with_all_type",
      TWO_TYPE_USERS_ADDED_REMOVED: "common:added_member_with_two_type",
      ONE_TYPE_USER_ADDED_REMOVED: "common:added_member_with_one_type",
    },
    REMOVE: {
      JOINED_LEFT_CHANNEL: "common:has_removed_the_channel",
      MULTIPLE_JOINED_LEFT_CHANNEL: "common:has_left_the_channel_with_others",
      ALL_TYPE_USERS_ADDED_REMOVED: "common:removed_member_with_all_type",
      TWO_TYPE_USERS_ADDED_REMOVED: "common:removed_member_with_two_type",
      ONE_TYPE_USER_ADDED_REMOVED: "common:removed_member_with_one_type",
    },
  };

  //if user types are same and creator also included in target then message will be "Joined conversation"
  //else message will be <UserName> added <UserName>, below is logic for same
  const sameTargetUserType =
    _.size(staff) === targetUserSize ||
    _.size(student) === targetUserSize ||
    _.size(parent) === targetUserSize;

  const createdById = createdBy?.id;

  const targetUsersIds = useMemo(() => _.map(targetUsers, user => user.id), [
    targetUsers,
  ]);
  const isCreatorIncludeInTarget = useMemo(
    () => _.includes(targetUsersIds, createdById),
    [targetUsersIds, createdById]
  );
  const targetUserTypeCount = _.size(_.keys(userGroupBy));

  if (sameTargetUserType && isCreatorIncludeInTarget) {
    const { value } = getActivityText({ userGroupBy, index: 0, t });
    return ifSingleTargetUser ? (
      <div className={activityTextClass}>
        {t(DESCRIPTION_TYPE_LOCALE_MAP[type].JOINED_LEFT_CHANNEL)}
      </div>
    ) : (
      <div className={activityTextClass}>
        <Trans
          i18nKey={
            DESCRIPTION_TYPE_LOCALE_MAP[type].MULTIPLE_JOINED_LEFT_CHANNEL
          }
        >
          <UsersDropdown
            t={t}
            options={_.filter(value, user => !isIdEqual(user.id, createdById))}
          >
            {{ number: _.size(value) - 1 }}
          </UsersDropdown>
        </Trans>
      </div>
    );
  }

  if (targetUserTypeCount === 3) {
    return (
      <div className={activityTextClass}>
        <Trans
          i18nKey={
            DESCRIPTION_TYPE_LOCALE_MAP[type].ALL_TYPE_USERS_ADDED_REMOVED
          }
        >
          <UsersDropdown
            options={getActivityText({ userGroupBy, index: 0, t }).value}
            t={t}
          >
            {{
              count_1: getActivityText({ userGroupBy, index: 0, t }).label,
            }}
          </UsersDropdown>
          <UsersDropdown
            options={getActivityText({ userGroupBy, index: 1, t }).value}
            t={t}
          >
            {{
              count_2: getActivityText({ userGroupBy, index: 1, t }).label,
            }}
          </UsersDropdown>
          <UsersDropdown
            options={getActivityText({ userGroupBy, index: 2, t }).value}
            t={t}
          >
            {{
              count_3: getActivityText({ userGroupBy, index: 2, t }).label,
            }}
          </UsersDropdown>
        </Trans>
      </div>
    );
  }

  if (targetUserTypeCount === 2) {
    return (
      <div className={activityTextClass}>
        <Trans
          i18nKey={
            DESCRIPTION_TYPE_LOCALE_MAP[type].TWO_TYPE_USERS_ADDED_REMOVED
          }
        >
          <UsersDropdown
            options={getActivityText({ userGroupBy, index: 0, t }).value}
            t={t}
          >
            {{
              count_1: getActivityText({ userGroupBy, index: 0, t }).label,
            }}
          </UsersDropdown>
          <UsersDropdown
            options={getActivityText({ userGroupBy, index: 1, t }).value}
            t={t}
          >
            {{
              count_2: getActivityText({ userGroupBy, index: 1, t }).label,
            }}
          </UsersDropdown>
        </Trans>
      </div>
    );
  }

  return (
    <div className={activityTextClass}>
      <Trans
        i18nKey={DESCRIPTION_TYPE_LOCALE_MAP[type].ONE_TYPE_USER_ADDED_REMOVED}
      >
        <UsersDropdown
          options={getActivityText({ userGroupBy, index: 0, t }).value}
          t={t}
        >
          {{ count_1: getActivityText({ userGroupBy, index: 0, t }).label }}
        </UsersDropdown>
      </Trans>
    </div>
  );
};

/**
 * This code follows new UI revemp of activity
 */
const MessageActivity = props => {
  const { message } = props;
  const activity = _.get(message, "channelActivity", {});
  const createdBy = _.get(message, "createdBy", {});
  const { type, after, targetUsers } = activity;

  const activityTextClass = classNames("text-body-2", classes.activityText);
  const activityTextWithoutFlex = classNames(
    "text-body-2",
    classes.activityTextWithoutFlex
  );
  const activityTextBoldClass = classNames(
    "text-label-3",
    classes.activityTextBold
  );

  switch (type) {
    case CHANNEL_ACTIVITY_TYPES.DESCRIPTION_CHANGE:
      return (
        <div className={activityTextWithoutFlex}>
          <Trans i18nKey={"common:updated_channel_desc_to_new_desc"}>
            <span className={activityTextBoldClass}>{{ label: after }}</span>
          </Trans>
        </div>
      );
    case CHANNEL_ACTIVITY_TYPES.LABEL_CHANGE:
      return (
        <div className={activityTextWithoutFlex}>
          <Trans i18nKey={"common:updated_channel_name_to_new_name"}>
            <span className={activityTextBoldClass}>{{ label: after }}</span>
          </Trans>
        </div>
      );

    case CHANNEL_ACTIVITY_TYPES.USER_ADD: {
      return (
        <ActivityTextComponent
          createdBy={createdBy}
          targetUsers={targetUsers}
          activityTextClass={activityTextClass}
          type="ADD"
        />
      );
    }

    case CHANNEL_ACTIVITY_TYPES.USER_REMOVE:
      return (
        <ActivityTextComponent
          createdBy={createdBy}
          targetUsers={targetUsers}
          activityTextClass={activityTextClass}
          type="REMOVE"
        />
      );
    default:
      return null;
  }
};

const MessageActivityWithDate = props => {
  const { message } = props;

  const createdBy = _.get(message, "createdBy", {});
  const {
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
  } = useMessageOptions();

  const fullName = getFormattedNameForUserType({
    user: createdBy,
    orderBy: defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    isFormattedNameV2: true,
  });

  return (
    <div className={classes.activityContainerV2}>
      <UserAvatar
        user={createdBy}
        fullName={fullName}
        avatarRenderMode={AVATAR_RENDER_MODES.AVATAR}
      />
      <div className={classes.activityDetails}>
        <UserName
          user={createdBy}
          fullName={fullName}
          createdAt={message?.createdAt}
        />
        <MessageActivity message={message} />
      </div>
    </div>
  );
};

export default MessageActivityWithDate;
